/**
 * #428 (SPEC-425 section 6): imported insurer commission grids.
 *
 * The wizard re-sends the workbook on each step (detect, save mapping, check)
 * instead of the server caching it, so nothing half-imported is ever held
 * server-side: a file is either rejected with every problem listed, or staged
 * as a `ready` version that only goes live on Publish.
 */
import api from './api';

export type GridSheetRole =
  | 'rates_matrix'
  | 'rates_long'
  | 'conditions'
  | 'incremental'
  | 'class_mapper'
  | 'decline_rto'
  | 'ignore';

export interface GridBasisConfig {
  source: 'row' | 'sheet_text' | 'declared';
  cell?: string;
  value?: string | null;
}

/** One sheet's mapping. Matches `grid_import` on the server; extra keys are kept as-is. */
export interface GridSheetConfig {
  title?: string;
  role: GridSheetRole;
  header_rows?: number[];
  column_fields?: Record<string, string>;
  key_header_row?: number | null;
  data_start?: number;
  data_end?: number | null;
  key_columns?: Record<string, string>;
  rate_columns?: string;
  rate_column?: string;
  ignore_columns?: string[];
  ignore_rows?: number[];
  lob?: string | null;
  class_from_mapper?: boolean;
  value_kind?: 'points' | 'fraction';
  basis?: GridBasisConfig;
  [key: string]: unknown;
}

export interface GridLayoutConfig {
  sheets: Record<string, GridSheetConfig>;
}

export type GridAliases = Record<
  string,
  Record<string, Record<string, unknown> | Record<string, unknown>[]>
>;

export interface GridSheetInfo {
  title: string;
  key: string | null;
  hidden: boolean;
  max_row: number;
  max_column: number;
  preview: string[][];
}

export interface GridLayout {
  id: string;
  name: string;
  insurer_id: string;
  version: number;
  fingerprint: string;
  config: GridLayoutConfig;
  aliases: GridAliases;
  declared_bases: { sheet: string; basis: string }[];
}

export interface GridDetectResult {
  file_name: string;
  sheets: GridSheetInfo[];
  layout: GridLayout | null;
  suggested_config: GridLayoutConfig;
  fields: string[];
  roles: GridSheetRole[];
  lobs: string[];
  premium_bases: string[];
  existing_grids: { id: string; name: string }[];
  /** #651: versions staged on an earlier attempt, awaiting publish or discard. */
  staged_versions?: {
    id: string;
    grid_name: string;
    version_no: number;
    file_name: string;
    created_at: string;
  }[];
}

export interface GridProblem {
  sheet: string;
  cell: string;
  row: number | null;
  value: string;
  code: string;
  message: string;
  field?: string;
  count?: number;
}

export interface GridImportRun {
  id: string;
  status: 'rejected' | 'ready';
  file_name: string;
  error_count: number;
  warning_count: number;
}

export interface GridVersionSummary {
  id: string;
  grid_id: string;
  grid_name: string;
  version_no: number;
  status: 'draft' | 'rejected' | 'ready' | 'active' | 'withdrawn';
  effective_from: string;
  effective_until: string | null;
  lobs: string[];
  file_name: string;
  /** #589: the uploaded workbook is stored and can be downloaded. */
  has_file?: boolean;
  row_count: number | null;
}

/** #429: one party's money before vs after a grid recalculation. */
export type GridRecalcAmounts = Partial<
  Record<'total' | 'broker' | 'posp' | 'super_posp' | 'platform', string>
>;

export interface GridRecalcItem {
  id: string;
  policy_id: string;
  policy_number: string;
  policy_start: string | null;
  outcome:
    | 'changed'
    | 'unchanged'
    | 'new'
    | 'manual_rate_kept'
    | 'no_rate'
    | 'skipped'
    | 'skipped_cancelled'
    | 'error';
  was_paid: boolean;
  before: GridRecalcAmounts;
  after: GridRecalcAmounts;
  delta: GridRecalcAmounts;
  message: string;
  rate_source: string;
  applied: boolean;
}

/** #467: classes or dates this grid family stops pricing when the version goes live. */
export interface GridCoverageLoss {
  kind: 'classes' | 'dates';
  classes: string[];
  from: string;
  to: string | null;
  previous_version: string;
}

/** #429 (D11): every policy in a grid's period, priced before it goes live. */
export interface GridRecalcBatch {
  id: string;
  version_id: string;
  grid_name: string;
  version_no: number;
  trigger: 'grid_activation' | 'revert';
  status: 'previewed' | 'stale' | 'applied' | 'reverted' | 'discarded';
  scope_from: string;
  scope_to: string | null;
  counts: Record<string, number>;
  totals: Record<
    'all' | 'paid' | 'unpaid',
    Record<string, { before: string; after: string; delta: string }>
  >;
  coverage_loss?: GridCoverageLoss[];
  error: string;
  created_at: string;
  applied_at: string | null;
  reverted_at: string | null;
  reverts_batch: string | null;
  items?: GridRecalcItem[];
}

export type GridPublishResult = GridVersionSummary & { recalc_batch?: GridRecalcBatch };

export interface GridSampleRow {
  id: string;
  source_ref: string;
  sheet: string;
  lob: string;
  class_group: string;
  insurer_segment: string;
  policy_type: string;
  geo_level: string;
  geo_label: string;
  rto_count: number;
  rate: string;
  premium_basis: string;
  basis_source: 'row' | 'sheet_text' | 'mapping_declared';
  basis_ref: string;
  constraints: Record<string, unknown>;
}

export interface GridSheetStats {
  title: string;
  role: string;
  rows: number;
  ignored_rows?: { row: number; reason: string }[];
  ignored_columns?: string[];
}

export interface GridPreview {
  status?: 'ready' | 'duplicate';
  duplicate?: boolean;
  version: GridVersionSummary;
  sheets: GridSheetStats[];
  row_count: number;
  by_class: Record<string, { rows: number; bases: Record<string, number> }>;
  sample_rows: GridSampleRow[];
  conditions: {
    id: string;
    sheet: string;
    source_ref: string;
    text: string;
    scope: string;
    enforced: boolean;
    not_enforced_reason: string;
  }[];
  declines: Record<string, { count: number; rto_count: number }>;
  slabs: {
    class_group: string;
    from_amount: string;
    to_amount: string | null;
    increment_points: string;
    volume_basis: string;
    source_ref: string;
  }[];
  warnings: GridProblem[];
  import_run_id: string | null;
}

export interface GridRejection {
  status: 'rejected';
  import_run: GridImportRun;
  problems: GridProblem[];
  warnings: GridProblem[];
}

export type GridUploadResult = GridPreview | GridRejection;

export interface GridUploadParams {
  file: File;
  insurerId: string;
  layoutId: string;
  effectiveFrom: string;
  effectiveUntil?: string;
  gridId?: string;
  name?: string;
  basisConfirmed: boolean;
}

export interface GridRegionInput {
  insurerId: string;
  label: string;
  kind: 'rto_set' | 'city' | 'metro_set' | 'zone' | 'state' | 'multi_state';
  rtoCodes?: string[];
  states?: string[];
  lob?: string;
}

/** #434 (SPEC-434): grid pages. */
export interface GridVersionDetail extends GridVersionSummary {
  insurer_id: string;
  activated_at: string | null;
  activated_by: string | null;
  withdrawn_at: string | null;
  withdrawn_by: string | null;
  withdrawn_reason: string;
  uploaded_by: string | null;
  uploaded_at: string | null;
}

export interface GridListItem {
  id: string;
  name: string;
  insurer_id: string;
  insurer_name: string;
  broker_id: string;
  broker_name: string;
  versions: GridVersionDetail[];
}

export interface GridRowFilters {
  lob?: string;
  region?: string;
  age?: string;
  fuel?: string;
  cc?: string;
  gvw?: string;
  policy_type?: string;
  ncb?: string;
  basis?: string;
  search?: string;
}

export interface Paginated<T> {
  count: number;
  results: T[];
}

export type GridCompareStatus =
  | 'changed'
  | 'basis_changed'
  | 'region_changed'
  | 'added'
  | 'removed';

export interface GridCompareEntry {
  status: GridCompareStatus;
  lob: string;
  class_group: string;
  insurer_segment: string;
  policy_type: string;
  geo_label: string;
  constraints: Record<string, unknown>;
  old: { rate: string; premium_basis: string; source_ref: string } | null;
  new: { rate: string; premium_basis: string; source_ref: string } | null;
  difference: string | null;
}

export interface GridCompareResult extends Paginated<GridCompareEntry> {
  counts: Record<GridCompareStatus | 'total', number>;
  from: GridVersionDetail;
  to: GridVersionDetail;
}

export interface GridUsage {
  grid_id: string;
  broker_id: string;
  broker_name: string;
  classes: string[];
  versions: {
    version_id: string;
    version_no: number;
    status: string;
    policies: number;
    premium: string;
  }[];
}

export interface GridRetirePreview {
  policies: number;
  commission: string;
  message: string;
}

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

export function isRejection(result: GridUploadResult): result is GridRejection {
  return (result as GridRejection).status === 'rejected';
}

export const commissionGridService = {
  async detect(file: File, insurerId: string): Promise<GridDetectResult> {
    const form = new FormData();
    form.append('file', file);
    form.append('insurer', insurerId);
    const res = await api.post<GridDetectResult>('/commissions/grids/detect/', form, multipart);
    return res.data;
  },

  async saveLayout(
    file: File,
    insurerId: string,
    name: string,
    config: GridLayoutConfig,
    aliases: GridAliases
  ): Promise<GridLayout> {
    const form = new FormData();
    form.append('file', file);
    form.append('insurer', insurerId);
    form.append('name', name);
    form.append('config', JSON.stringify(config));
    form.append('aliases', JSON.stringify(aliases));
    const res = await api.post<GridLayout>('/commissions/grid-layouts/', form, multipart);
    return res.data;
  },

  async addAliases(layoutId: string, aliases: GridAliases): Promise<GridLayout> {
    const res = await api.post<GridLayout>(`/commissions/grid-layouts/${layoutId}/aliases/`, {
      aliases,
    });
    return res.data;
  },

  async saveRegion(input: GridRegionInput): Promise<void> {
    await api.post('/commissions/grid-region-maps/', {
      insurer: input.insurerId,
      label: input.label,
      kind: input.kind,
      rto_codes: input.rtoCodes ?? [],
      states: input.states ?? [],
      lob: input.lob ?? '',
    });
  },

  /**
   * 422 (rejected, every problem listed) is an ANSWER here, not a failure: it is
   * returned as a `GridRejection` so the screen can show the full problem list.
   */
  async upload(params: GridUploadParams): Promise<GridUploadResult> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('insurer', params.insurerId);
    form.append('layout', params.layoutId);
    form.append('effective_from', params.effectiveFrom);
    if (params.effectiveUntil) form.append('effective_until', params.effectiveUntil);
    if (params.gridId) form.append('grid', params.gridId);
    if (params.name) form.append('name', params.name);
    form.append('basis_confirmed', params.basisConfirmed ? 'true' : 'false');
    const res = await api.post<GridUploadResult>('/commissions/grids/upload/', form, {
      ...multipart,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 422,
    });
    return res.data;
  },

  /** #429: price every policy in the version's period with it live; nothing else is written. */
  async recalcPreview(versionId: string): Promise<GridRecalcBatch> {
    const res = await api.post<GridRecalcBatch>(
      `/commissions/grid-versions/${versionId}/recalc-preview/`
    );
    return res.data;
  },

  /** Publishing applies the reviewed recalculation batch (D11): the version goes live with it. */
  async publish(versionId: string, batchId?: string): Promise<GridPublishResult> {
    const res = await api.post<GridPublishResult>(
      `/commissions/grid-versions/${versionId}/publish/`,
      batchId ? { batch: batchId } : undefined
    );
    return res.data;
  },

  /** Put every policy back to its amounts before the batch; the version awaits publication again. */
  async revertBatch(batchId: string): Promise<GridRecalcBatch> {
    const res = await api.post<GridRecalcBatch>(
      `/commissions/grid-recalc-batches/${batchId}/revert/`
    );
    return res.data;
  },

  async listGrids(
    params: { insurer?: string; broker?: string; lob?: string; status?: string } = {}
  ) {
    const res = await api.get<{ results: GridListItem[] }>('/commissions/grids/', { params });
    return res.data.results;
  },

  async getGrid(id: string): Promise<GridListItem> {
    const res = await api.get<GridListItem>(`/commissions/grids/${id}/`);
    return res.data;
  },

  async usage(gridId: string): Promise<GridUsage> {
    const res = await api.get<GridUsage>(`/commissions/grids/${gridId}/usage/`);
    return res.data;
  },

  async holders(insurerId: string) {
    const res = await api.get<{
      results: { broker_id: string; broker_name: string; live_versions: number }[];
    }>('/commissions/grids/holders/', { params: { insurer: insurerId } });
    return res.data.results;
  },

  async getVersion(id: string): Promise<GridPreview> {
    const res = await api.get<GridPreview>(`/commissions/grid-versions/${id}/`);
    return res.data;
  },

  async rows(versionId: string, filters: GridRowFilters, page: number) {
    const params = { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), page };
    const res = await api.get<Paginated<GridSampleRow>>(
      `/commissions/grid-versions/${versionId}/rows/`,
      { params }
    );
    return res.data;
  },

  async rowsCsv(versionId: string, filters: GridRowFilters): Promise<Blob> {
    const params = {
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
      export: 'csv',
    };
    const res = await api.get(`/commissions/grid-versions/${versionId}/rows/`, {
      params,
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  async compare(
    from: string,
    to: string,
    status: string,
    page: number
  ): Promise<GridCompareResult> {
    const res = await api.get<GridCompareResult>('/commissions/grid-versions/compare/', {
      params: { from, to, page, ...(status ? { status } : {}) },
    });
    return res.data;
  },

  async compareCsv(from: string, to: string, status: string): Promise<Blob> {
    const res = await api.get('/commissions/grid-versions/compare/', {
      params: { from, to, export: 'csv', ...(status ? { status } : {}) },
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  /** Without `confirm` the server answers 409 `retire_preview_required` with what the withdrawal touches. */
  async retirePreview(versionId: string, reason: string): Promise<GridRetirePreview> {
    const res = await api.post<{ preview: GridRetirePreview }>(
      `/commissions/grid-versions/${versionId}/retire/`,
      { reason },
      { validateStatus: (s) => s === 409 }
    );
    return res.data.preview;
  },

  async retire(versionId: string, reason: string): Promise<GridVersionDetail> {
    const res = await api.post<GridVersionDetail>(
      `/commissions/grid-versions/${versionId}/retire/`,
      {
        reason,
        confirm: true,
      }
    );
    return res.data;
  },

  async discard(versionId: string): Promise<void> {
    await api.post(`/commissions/grid-versions/${versionId}/discard/`);
  },

  /** #589: the original uploaded workbook (admins of the grid's broker only). */
  async downloadOriginal(versionId: string): Promise<Blob> {
    const res = await api.get(`/commissions/grid-versions/${versionId}/download/`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  async problemsReport(runId: string): Promise<Blob> {
    const res = await api.get(`/commissions/grid-import-runs/${runId}/problems.csv/`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },
};
